import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { apiOkr } from 'app/config/axios-interceptor';
import { IKr, defaultValue } from 'app/shared/model/kr.model';
import { EntityState, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IKr> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  loadingAdjustmentMidYearQualitative: false,
  adjustmentMidYearQualitative: {},
};

const apiUrl = 'api/okr/key-result';

// Actions

export const createEntity = createAsyncThunk(
  'key-result-qualitative/create_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.post<IKr>(`${apiUrl}/qualitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const copyEntity = createAsyncThunk(
  'key-result-qualitative/copy_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.post<IKr>(`${apiUrl}/qualitative-ver2`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createKrQual = createAsyncThunk(
  'key-result-qualitative/create_kr_qual',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.post<IKr>(`${apiUrl}/qualitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'key-result-qualitative/update_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.put<IKr>(`${apiUrl}/qualitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getAdjustmentMidYearQualitative = createAsyncThunk(
  'key-result-qualitative/get_adjustment_mid_year_qualitative',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.get<any>(`/api/okr/year-planning/adjustment-mid-year-qualitative?krId=${entity.krId}&planPeriodId=${entity.planPeriodId}`);
    return result;
  },
  { serializeError: serializeAxiosError },
);


export const createAdjustmentMidYearQualitative = createAsyncThunk(
  'key-result-qualitative/create_adjustment_mid_year_qualitative',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>('/api/okr/year-planning/adjustment-mid-year-qualitative', entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateCopyEntity = createAsyncThunk(
  'key-result-qualitative/update_copy_entity',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.put<IKr>(`${apiUrl}/year-planning/adjustment-mid-year-qualitative`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateCopyEntityVer2= createAsyncThunk(
  'key-result-qualitative/update_copy_entity_ver2',
  async (entity: IKr, thunkAPI) => {
    const result = await apiOkr.put<IKr>(`${apiUrl}/qualitative-ver2`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQualAdjust = createAsyncThunk(
  'key-result-qualitative/key_result_qual_adjustment_mid_year',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/adjustment-mid-year-qualitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQualHalfMonthAdjust = createAsyncThunk(
  'key-result-qualitative/key_result_qual_adjustment_half_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/adjustment-half-month-qualitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const checkInKrQualMonthAdjust = createAsyncThunk(
  'key-result-qualitative/key_result_qual_adjustment_month',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.post<any>(`${apiUrl}/adjustment-month-qualitative`, entity);
    return result;
  },
  { serializeError: serializeAxiosError },
);

type IDetailKr = {
  idKrBaseSelected?: string | number;
  planPeriodId?: string | number;
  okrVersion?: number;
};

export const getEntity = createAsyncThunk(
  'key-result/fetch_entity_qualitative',
  async ({ planPeriodId, idKrBaseSelected }: IDetailKr) => {
    const requestUrl = `${apiUrl}/qualitative-ver2?krId=${idKrBaseSelected}&planPeriodId=${planPeriodId}`;
    return apiOkr.get<IKr>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getCopyEntity = createAsyncThunk(
  'key-result/fetch_copy_entity_qualitative',
  async ({ planPeriodId, idKrBaseSelected, okrVersion }: IDetailKr) => {
    const requestUrl = `${apiUrl}/kr-qual-copy-this`;
    return apiOkr.get<IKr>(requestUrl, {
      params: { krId: idKrBaseSelected, okrVersion, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCopyEntityNextPlanPeriod = createAsyncThunk(
  'key-result/fetch_copy_entity_qualitative_next_plan_period',
  async ({ planPeriodId, idKrBaseSelected, okrVersion }: IDetailKr) => {
    const requestUrl = `${apiUrl}/kr-qual-copy-this-to-next-year`;
    return apiOkr.get<IKr>(requestUrl, {
      params: { krId: idKrBaseSelected, okrVersion, planPeriodId },
    });
  },
  { serializeError: serializeAxiosError },
);

// slice

export const KrQualitativeSlice = createEntitySlice({
  name: 'key-result-qualitative',
  initialState,
  reducers: {
    resetAdjustmentMidYearQualitative(state) {
      state.adjustmentMidYearQualitative = {};
      state.loadingAdjustmentMidYearQualitative = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getCopyEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getCopyEntityNextPlanPeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(createEntity, copyEntity, updateEntity, updateCopyEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntity, getCopyEntity, getCopyEntityNextPlanPeriod), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(createEntity, copyEntity, updateEntity, updateCopyEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(getAdjustmentMidYearQualitative), state => {
        state.loadingAdjustmentMidYearQualitative = true;
      })
      .addMatcher(isFulfilled(getAdjustmentMidYearQualitative), (state, action) => {
        state.loadingAdjustmentMidYearQualitative = false;
        state.adjustmentMidYearQualitative = action.payload.data;
      })
      .addMatcher(isRejected(getAdjustmentMidYearQualitative), (state, action) => {
        state.loadingAdjustmentMidYearQualitative = false;
        state.errorMessage = action.error.message;
      });
    
  },
});

export const { reset,resetAdjustmentMidYearQualitative } = KrQualitativeSlice.actions;

// Reducer
export default KrQualitativeSlice.reducer;
